(() => {
  const drawerSelector = '[data-ac-cart-drawer]';
  let lastTrigger = null;
  let busy = false;

  const drawer = () => document.querySelector(drawerSelector);
  const sectionId = () => drawer()?.dataset.sectionId;
  const rootUrl = () => (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
  const route = (path) => `${rootUrl()}/${String(path).replace(/^\//, '')}`;

  const updateHeaderCount = (count) => {
    document.querySelectorAll('[data-header-cart-count]').forEach((el) => {
      const value = Number(count) || 0;
      el.dataset.headerCartCount = value ? String(value) : '';
      el.textContent = value ? String(value) : '';
      el.classList.toggle('visible', value > 0);
    });
  };

  const setLoading = (state) => {
    busy = state;
    drawer()?.classList.toggle('is-loading', state);
  };

  const openDrawer = () => {
    const el = drawer();
    if (!el) return;
    el.hidden = false;
    el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('ac-cart-drawer-open');
    requestAnimationFrame(() => {
      el.classList.add('is-open');
      el.querySelector('[data-ac-cart-drawer-close]')?.focus({ preventScroll: true });
    });
  };

  const closeDrawer = () => {
    const el = drawer();
    if (!el) return;
    el.classList.remove('is-open');
    document.documentElement.classList.remove('ac-cart-drawer-open');
    window.setTimeout(() => {
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
      lastTrigger?.focus?.({ preventScroll: true });
    }, 290);
  };

  const refreshDrawer = async ({ open = true } = {}) => {
    const id = sectionId();
    if (!id) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sections', id);
    url.searchParams.set('_ac_cart_drawer', Date.now().toString());
    const res = await fetch(url.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!res.ok) throw new Error('Unable to refresh cart drawer');
    const sections = await res.json();
    const html = sections[id];
    if (!html) throw new Error('Cart drawer section missing');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector(drawerSelector);
    const current = drawer();
    if (!next || !current) throw new Error('Cart drawer markup missing');
    current.replaceWith(next);
    if (open) openDrawer();
  };

  const getCart = async () => {
    const res = await fetch(route('cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Unable to load cart');
    return res.json();
  };

  const changeLine = async (key, quantity) => {
    if (busy) return;
    setLoading(true);
    try {
      const res = await fetch(route('cart/change.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json();
      if (!res.ok) throw new Error(cart.description || cart.message || 'Unable to update cart');
      updateHeaderCount(cart.item_count);
      await refreshDrawer({ open: true });
      document.dispatchEvent(new CustomEvent('ac-cart:updated', { detail: cart }));
    } finally {
      setLoading(false);
    }
  };

  const lineQuantity = (button) => {
    const item = button.closest('[data-ac-drawer-line-key]');
    return Number(item?.querySelector('.ac-cart-drawer-qty span')?.textContent.trim()) || 0;
  };


  const offerModal = () => document.querySelector('[data-ac-cart-offer-modal]');
  const openOfferModal = () => {
    const modal = offerModal();
    if (!modal) return;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('ac-cart-offer-open');
    requestAnimationFrame(() => {
      modal.classList.add('is-open');
      modal.querySelector('[data-ac-cart-offer-close]')?.focus({ preventScroll: true });
    });
  };
  const closeOfferModal = () => {
    const modal = offerModal();
    if (!modal) return;
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('ac-cart-offer-open');
    window.setTimeout(() => {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
    }, 220);
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest?.('[data-ac-direct-add-form]');
    if (!form) return;

    event.preventDefault();
    if (form.dataset.acAdding === 'true') return;

    const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
    if (!variantId) return;

    const button = form.querySelector('button[type="submit"]');
    const originalText = button?.textContent || 'Add to Cart';
    form.dataset.acAdding = 'true';
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding…';
    }

    try {
      const res = await fetch(route('cart/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result.description || result.message || 'Unable to add item to cart');

      const cart = await getCart();
      updateHeaderCount(cart.item_count);
      await refreshDrawer({ open: true });
      document.dispatchEvent(new CustomEvent('ac-cart:updated', { detail: cart }));
      if (button) button.textContent = 'Added';
    } catch (error) {
      console.error('AC direct add:', error);
      if (button) button.textContent = 'Try Again';
    } finally {
      window.setTimeout(() => {
        form.dataset.acAdding = 'false';
        if (button) {
          button.disabled = false;
          button.textContent = originalText;
        }
      }, 1000);
    }
  });

  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-ac-cart-open]');
    const close = event.target.closest('[data-ac-cart-drawer-close]');
    const minus = event.target.closest('[data-ac-drawer-minus]');
    const plus = event.target.closest('[data-ac-drawer-plus]');
    const remove = event.target.closest('[data-ac-drawer-remove]');
    const offerOpen = event.target.closest('[data-ac-cart-offer-open]');
    const offerClose = event.target.closest('[data-ac-cart-offer-close]');
    const offerScroll = event.target.closest('[data-ac-cart-offer-scroll]');

    if (offerOpen) {
      event.preventDefault();
      openOfferModal();
      return;
    }

    if (offerClose) {
      event.preventDefault();
      closeOfferModal();
      return;
    }

    if (offerScroll) {
      event.preventDefault();
      closeDrawer();
      window.setTimeout(() => {
        document.querySelector('#ac-cart-free-shipping-offer')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
      return;
    }

    if (trigger) {
      event.preventDefault();
      lastTrigger = trigger;
      try {
        const cart = await getCart();
        updateHeaderCount(cart.item_count);
        await refreshDrawer({ open: true });
      } catch (error) {
        console.error('AC cart drawer:', error);
        openDrawer();
      }
      return;
    }

    if (close) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (minus || plus) {
      event.preventDefault();
      const button = minus || plus;
      const key = button.dataset.lineKey;
      if (!key) return;
      const current = lineQuantity(button);
      const quantity = Math.max(0, current + (plus ? 1 : -1));
      try { await changeLine(key, quantity); } catch (error) { console.error('AC cart drawer:', error); window.location.reload(); }
      return;
    }

    if (remove) {
      event.preventDefault();
      const key = remove.dataset.lineKey;
      if (!key) return;
      try { await changeLine(key, 0); } catch (error) { console.error('AC cart drawer:', error); window.location.reload(); }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && offerModal()?.classList.contains('is-open')) { closeOfferModal(); return; }
    if (event.key === 'Escape' && drawer()?.classList.contains('is-open')) closeDrawer();
  });

  // Empire handles the actual AJAX add. We suppress its visual banner in CSS,
  // then refresh/open the AC drawer as soon as the add succeeds.
  document.addEventListener('addToCartFlyout:complete', async () => {
    try {
      const cart = await getCart();
      updateHeaderCount(cart.item_count);
      await refreshDrawer({ open: true });
      document.dispatchEvent(new CustomEvent('ac-cart:updated', { detail: cart }));
    } catch (error) {
      console.error('AC cart drawer:', error);
    }
  });

  // Catch other cart implementations/apps that emit the common count event.
  window.addEventListener('cartcount:update', (event) => {
    const count = event.detail?.item_count ?? event.detail?.itemCount;
    if (typeof count === 'number') updateHeaderCount(count);
  });

  document.addEventListener('DOMContentLoaded', () => {
    if (drawer()) document.body.classList.add('ac-cart-drawer-enabled');
  });
  document.addEventListener('shopify:section:load', () => {
    if (drawer()) document.body.classList.add('ac-cart-drawer-enabled');
  });
})();
